import React, { useEffect, useMemo, useState } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'

import type {
  CreateDealReqBody,
  DealEntity,
  LineItemReqBody,
  UpdateDealReqBody,
} from '../../../apis/dealsAPI'
import { useToast } from '../../../hooks/useToast'
import { useStore } from '../../../stores/GlobalStore'
import { useCompanyOptionsQuery } from '../hooks/useCompanyOptionsQuery'
import { useCreateDealMutation } from '../hooks/useCreateDealMutation'
import { useCreateShipmentMutation } from '../hooks/useCreateShipmentMutation'
import { useDealQuery } from '../hooks/useDealQuery'
import { useDeleteShipmentMutation } from '../hooks/useDeleteShipmentMutation'
import { useHousesQuery } from '../hooks/useHousesQuery'
import { useProductsQuery } from '../hooks/useProductsQuery'
import { useShipmentsQuery } from '../hooks/useShipmentsQuery'
import { useUncancelDealMutation } from '../hooks/useUncancelDealMutation'
import { useUpdateDealMutation } from '../hooks/useUpdateDealMutation'
import { useUpdateShipmentMutation } from '../hooks/useUpdateShipmentMutation'
import { useModal, type EditDealTab as ModalEditDealTab } from '../state/ModalProvider'
import type { Shipment } from '../types/deal'
import { isDealArchived, isDealCanceled } from '../utils/dealUrgency'

import { GeneralTab } from './EditDealModal/GeneralTab'
import { LineItemsTab } from './EditDealModal/LineItemsTab'
import { ShipmentTab } from './EditDealModal/ShipmentTab'
import {
  billingOptionToCadence,
  buildInitialDraft,
  validateDealDraft,
} from './EditDealModal/helpers'
import {
  diffShipments,
  shipmentToCreateBody,
  shipmentToUpdateBody,
} from './EditDealModal/shipmentDiff'
import type { DealDraft, TouchedFields } from './EditDealModal/types'

const ALL_FIELDS_TOUCHED: TouchedFields = {
  companyId: true,
  businessId: true,
  customerName: true,
  customerEmail: true,
  customerPhone: true,
  customerEIN: true,
  shippingAddress: true,
  activationISO: true,
  expirationISO: true,
  pricingRegion: true,
  pricingTerm: true,
  quoteNo: true,
  quoteUrl: true,
  billingCount: true,
  billingType: true,
  autoSendOn: true,
  ccExecOn: true,
  ccExecs: true,
  memo: true,
  lineItems: true,
}

interface EditDealModalProps {
  isNew: boolean
  dealId?: string
  initialTab?: ModalEditDealTab
  highlightShipmentId?: string
}

const TAB_ORDER: ModalEditDealTab[] = ['general', 'lineitems', 'shipping']

function lineItemsToPayload(draft: DealDraft): LineItemReqBody[] {
  return draft.lineItems.map((item) => ({
    type: item.type,
    label: item.badgeLabel,
    productKey: item.productValue ?? '',
    productName: item.name,
    qty: item.qty,
    assignments:
      item.type === 'sw' ? { houses: item.devices } : { devices: item.devices },
  }))
}

function buildCreateBody(draft: DealDraft, salesRepUserId: string): CreateDealReqBody {
  return {
    companyId: draft.companyId,
    businessId: draft.businessId,
    salesRepUserId,
    activationDate: draft.activationISO || undefined,
    expirationDate: draft.expirationISO || undefined,
    customerName: draft.customerName.trim(),
    customerEmail: draft.customerEmail.trim(),
    telephone: draft.customerPhone.trim() || undefined,
    einTaxId: draft.customerEIN.trim() || undefined,
    shippingAddress: draft.shippingAddress.trim() || undefined,
    billingCadence: billingOptionToCadence(draft.billingType),
    pricingRegion: draft.pricingRegion,
    pricingTerm: draft.pricingTerm,
    lineItems: lineItemsToPayload(draft),
    memo: draft.memo.trim() || undefined,
    ccExecOn: draft.ccExecOn,
    ccExecs: draft.ccExecs,
  }
}

function buildUpdateBody(draft: DealDraft, salesRepUserId: string): UpdateDealReqBody {
  return buildCreateBody(draft, salesRepUserId)
}

export const EditDealModal: React.FunctionComponent<EditDealModalProps> = (props) => {
  const { isNew, dealId, initialTab, highlightShipmentId } = props
  const { close, open, openFirstInvoice } = useModal()
  const toast = useToast()
  const { state } = useStore()

  const productsQuery = useProductsQuery()
  const companyOptionsQuery = useCompanyOptionsQuery()
  const dealQuery = useDealQuery(isNew ? null : dealId ?? null)

  const existingDeal: DealEntity | null = isNew ? null : dealQuery.data ?? null
  const dealKey = existingDeal?.id ?? '__new__'

  const shipmentsQuery = useShipmentsQuery(existingDeal?.id ?? null)

  const [draft, setDraft] = useState<DealDraft>(() => buildInitialDraft(isNew, existingDeal))
  const [tab, setTab] = useState<ModalEditDealTab>(
    initialTab && (!isNew || initialTab !== 'shipping') ? initialTab : 'general',
  )
  const [shipmentsOverride, setShipmentsOverride] = useState<Shipment[] | null>(null)
  const [touched, setTouched] = useState<TouchedFields>({})

  const housesQuery = useHousesQuery(draft.companyId)

  useEffect(() => {
    setDraft(buildInitialDraft(isNew, existingDeal))
    setTab(initialTab && (!isNew || initialTab !== 'shipping') ? initialTab : 'general')
    setShipmentsOverride(null)
    setTouched({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealKey, isNew, initialTab])

  const markTouched = (field: keyof DealDraft): void => {
    setTouched((current) => (current[field] ? current : { ...current, [field]: true }))
  }

  const validation = useMemo(() => validateDealDraft(draft), [draft])

  const createMutation = useCreateDealMutation()
  const updateMutation = useUpdateDealMutation()
  const uncancelMutation = useUncancelDealMutation()
  const createShipmentMutation = useCreateShipmentMutation()
  const updateShipmentMutation = useUpdateShipmentMutation()
  const deleteShipmentMutation = useDeleteShipmentMutation()

  function patch(next: Partial<DealDraft>): void {
    setDraft((current) => ({ ...current, ...next }))
  }

  const visibleTabs = TAB_ORDER.filter((entry) => !(isNew && entry === 'shipping'))
  const isLastTab = tab === visibleTabs[visibleTabs.length - 1]
  const saveCls = isLastTab ? 'btn-save' : 'btn-save-outline'
  const dealCanceled = existingDeal ? isDealCanceled(existingDeal) : false
  const dealArchived = existingDeal ? isDealArchived(existingDeal) : false
  const dealLocked = dealCanceled || dealArchived

  function goNext(): void {
    const index = visibleTabs.indexOf(tab)
    if (index >= 0 && index < visibleTabs.length - 1) setTab(visibleTabs[index + 1])
  }

  function showError(message: string): void {
    toast.current?.show({
      severity: 'error',
      summary: 'Validation',
      detail: message,
      sticky: true,
    })
  }

  async function handleSave(): Promise<void> {
    if (!validation.valid) {
      setTouched(ALL_FIELDS_TOUCHED)
      const firstError = Object.values(validation.errors)[0]
      if (firstError) showError(firstError)
      setTab('general')
      return
    }

    const salesRepUserId = (state.user as { id?: string } | undefined)?.id ?? ''
    if (!salesRepUserId) {
      showError('Sales rep user id unavailable — please re-login')
      return
    }

    if (isNew) {
      const body = buildCreateBody(draft, salesRepUserId)
      createMutation.mutate(body, {
        onSuccess: (deal) => {
          close()
          openFirstInvoice(deal.id)
        },
      })
      return
    }

    if (!existingDeal) return

    const dealIdForSave = existingDeal.id

    // Persist any shipment edits before patching the deal. Backend's deal
    // PATCH endpoint does not accept shipments — each create/update/delete
    // goes to /deals/:id/shipments/... and only after they all succeed do we
    // touch the deal itself, so a half-applied save never closes the modal.
    if (shipmentsOverride) {
      const originalShipments = shipmentsQuery.data ?? []
      const { toCreate, toUpdate, toDelete } = diffShipments(
        originalShipments,
        shipmentsOverride,
      )
      try {
        await Promise.all(
          toDelete.map((shipment) =>
            deleteShipmentMutation.mutateAsync({
              dealId: dealIdForSave,
              shipmentId: shipment.id,
            }),
          ),
        )
        await Promise.all(
          toUpdate.map((shipment) =>
            updateShipmentMutation.mutateAsync({
              dealId: dealIdForSave,
              shipmentId: shipment.id,
              body: shipmentToUpdateBody(shipment),
            }),
          ),
        )
        await Promise.all(
          toCreate.map((shipment) =>
            createShipmentMutation.mutateAsync({
              dealId: dealIdForSave,
              body: shipmentToCreateBody(shipment),
            }),
          ),
        )
      } catch {
        // useApiErrorHandler already surfaced the toast; abort the deal patch
        // so the user can retry from the same modal state.
        return
      }
    }

    const body = buildUpdateBody(draft, salesRepUserId)
    updateMutation.mutate(
      { dealId: dealIdForSave, body },
      {
        onSuccess: () => {
          close()
        },
      },
    )
  }

  function handleCancelSubClick(): void {
    if (!existingDeal) return
    if (dealCanceled) {
      uncancelMutation.mutate(existingDeal.id, {
        // Closing the edit modal mirrors the Confirm Cancellation flow so the
        // user lands back in the deals list and sees the row leave the
        // Canceled bucket.
        onSuccess: () => close(),
      })
      return
    }
    open({ kind: 'cancel-subscription', dealId: existingDeal.id })
  }

  function stop(event: ReactMouseEvent): void {
    event.stopPropagation()
  }

  const productGroups = productsQuery.data ?? []
  const houseTree = housesQuery.data ?? []
  const companyOptions = companyOptionsQuery.data ?? []
  const isMutating =
    createMutation.isLoading ||
    updateMutation.isLoading ||
    createShipmentMutation.isLoading ||
    updateShipmentMutation.isLoading ||
    deleteShipmentMutation.isLoading
  const saveDisabled = isMutating || !validation.valid || dealLocked

  const dealShipments = shipmentsOverride ?? shipmentsQuery.data ?? []

  return (
    <div className="modal-overlay active" onClick={close}>
      <div className="modal" style={{ width: 680 }} onClick={stop}>
        <div className="modal-header">
          <div className="modal-header-left">
            <span className="modal-title">{isNew ? 'New Deal' : 'Edit Deal'}</span>
            <span className="modal-deal-num">{isNew ? 'New' : existingDeal?.orderNumber ?? ''}</span>
          </div>
          <button type="button" className="modal-close" onClick={close} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-tabs">
          <div
            className={`modal-tab${tab === 'general' ? ' active' : ''}`}
            onClick={() => setTab('general')}
          >
            General
          </div>
          <div
            className={`modal-tab${tab === 'lineitems' ? ' active' : ''}`}
            onClick={() => setTab('lineitems')}
          >
            Line Items
          </div>
          <div
            className={`modal-tab${tab === 'shipping' ? ' active' : ''}`}
            onClick={() => setTab('shipping')}
            style={{ display: isNew ? 'none' : '' }}
          >
            Shipment
          </div>
        </div>
        <div className="modal-body">
          {dealLocked && (
            <div className="deal-edit-lock-banner">
              {dealCanceled
                ? 'This deal is canceled. Reactivate the subscription to edit.'
                : 'This deal is archived.'}
            </div>
          )}
          {tab === 'general' && (
            <GeneralTab
              draft={draft}
              patch={patch}
              isNew={isNew}
              companyOptions={companyOptions}
              validation={validation}
              touched={touched}
              markTouched={markTouched}
            />
          )}
          {tab === 'lineitems' && (
            <LineItemsTab
              draft={draft}
              patch={patch}
              productGroups={productGroups}
              houseTree={houseTree}
              onError={showError}
            />
          )}
          {tab === 'shipping' && !isNew && existingDeal && (
            <ShipmentTab
              shipments={dealShipments}
              highlightShipmentId={highlightShipmentId}
              onChangeShipments={(next) => setShipmentsOverride(next)}
              onError={showError}
            />
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className={`btn-cancel-sub${dealCanceled ? ' reactivate' : ''}`}
            style={{ display: isNew ? 'none' : '' }}
            onClick={handleCancelSubClick}
            disabled={uncancelMutation.isLoading}
          >
            {dealCanceled
              ? uncancelMutation.isLoading
                ? 'Reactivating…'
                : 'Reactivate Subscription'
              : 'Cancel Subscription'}
          </button>
          <div className="footer-right">
            <button type="button" className="btn-ghost" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className={saveCls}
              onClick={handleSave}
              disabled={saveDisabled}
            >
              {isMutating ? 'Saving…' : 'Save'}
            </button>
            {!isLastTab && (
              <button
                type="button"
                className="btn-save"
                onClick={goNext}
                disabled={dealLocked}
              >
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
